/**
 * lazyMitNeuladen — eine lazy Route, die einen veralteten Chunk selbst heilt.
 *
 * @description
 *   Seit die Routen einzeln laden (`App.js`, Routen-Split 2026-10-03) verlangt
 *   ein ueber ein Deployment offen gebliebener Tab einen Chunk-Hash, den der
 *   neue Bau nicht mehr kennt: die Anfrage geht ins Netz und endet als
 *   `ChunkLoadError`. Bis 2026-10-10 zeigte die `ErrorBoundary` dann nur „This
 *   page could not be loaded", und der Bediener musste selbst neu laden.
 *
 *   Dieser Baustein laedt stattdessen EINMAL selbst neu — und nur einmal: der
 *   Zeitstempel des letzten Neuladens liegt in `sessionStorage` (je Tab), und
 *   innerhalb des Fensters wird nicht ein zweites Mal geladen, sondern der
 *   Fehler durchgereicht. So faengt der Bediener eine echte Stoerung
 *   (Deploy-Fenster, abgerissene Verbindung) auf der bekannten Fehlerkarte,
 *   statt in einer Neuladeschleife zu landen.
 *
 *   Ist `sessionStorage` nicht nutzbar (wirft beim Lesen ODER beim Schreiben),
 *   wird NICHT neu geladen — ohne Zeitstempel gaebe es keinen Schleifenschutz.
 *
 *   Nach `reload()` lehnt die Zusage nach einer kurzen Frist mit dem
 *   urspruenglichen Fehler ab. Bleibt die Navigation aus (etwa ein
 *   Hintergrund-Tab, den der Browser verzoegert), greift danach die
 *   `ErrorBoundary` mit Karte und Knopf — statt eines wortlosen Skeletts.
 *
 *   🔴 Nur fuer ROUTEN. Ein `lazy` TEILBAUM mitten in einer Seite
 *   (`ProductDetail.jsx`, `design/DesignCatalog.jsx`) will die oertliche Grenze,
 *   nicht das Seiten-Neuladen: `design/BestandGrenze.jsx` sagt das ausdruecklich,
 *   und die offene Karte
 *   `docs/backlog-auto-repair/2026-09-30-produktdetail-umsatzdiagramm-probe-hoehensprung-chunkfehler.md`
 *   verlangt fuer den Fall nur das Panel. Wer den Baustein dort einbaut, macht den
 *   „erneut versuchen"-Knopf zu totem Code.
 *
 * @useWhen
 *   - Als Huelle jeder Route, die per `React.lazy` geladen wird
 *
 * @dontUseFor
 *   - Ein `lazy` INNERHALB einer Seite — dort ist der Weg heraus oertlich
 *     (erneut versuchen), nicht das Neuladen der Seite
 *   - Alles, was kein dynamischer `import()` ist
 *
 * @related
 *   - `ErrorBoundary` — verbraucht `istChunkFehler` und zeigt die Karte, wenn
 *     das Neuladen ausblieb oder der Fehler im Fenster erneut auftrat
 *   - `RouteFallback` — die andere Haelfte des Paars: jener deckt „laedt noch"
 *   - `design/BestandGrenze.jsx` — die oertliche Grenze, die ein `lazy`
 *     INNERHALB einer Seite will (siehe `@dontUseFor`)
 *
 * @reuseHints
 *   - Jede neue Route in `App.js` huellt ihr `import()` hier ein
 *   - Ein kuenftiger Wiederholversuch auf Routenebene gehoert hierher, nicht in
 *     eine zweite Kopie
 */

import { meldeFehler } from './fehlerbericht';

/** Schluessel des Zeitstempels in `sessionStorage` — je Tab, nicht je Herkunft. */
export const CHUNK_NEULADEN_SCHLUESSEL = 'pog.chunkNeuladen';

/** Innerhalb dieses Fensters nach einem Neuladen wird kein zweites Mal geladen. */
const FENSTER_MS = 60000;

/** So lange haengt die Zusage nach `reload()`, bevor der Fehler durchkommt. */
const HAENGEN_MS = 5000;

/**
 * Das Muster, das einen Chunk-Fehler in der MELDUNG erkennt — EINE Erkennung,
 * nicht zwei. `ErrorBoundary` und `lazyMitNeuladen` benutzen dieselbe.
 *
 * 🔴 Die Chunk-Form verlangt eine KENNUNG: `Loading (CSS )?chunk <id> failed`.
 * Ein blosses „Loading chunk" genuegt NICHT — sonst faengt das Muster jeden
 * Satz, der zufaellig so beginnt („Loading chunk data for the dashboard widget
 * failed"), und ein Renderfehler loeste ein Neuladen aus. `<id>` ist eine
 * Ziffern-/Bezeichnerfolge ohne Leerzeichen (`[\w.-]+`); das deckt `941` so gut
 * wie einen benannten Chunk (`vendors-node_modules_…`).
 *
 * `Loading CSS chunk <id> failed` ist die Form des webpack-CSS-Laders
 * (mini-css-extract-plugin, in CRA-Produktionsbauten aktiv). Sie fiel vor dem
 * 2026-10-10 durch, weil zwischen `Loading ` und `chunk` ein `CSS` steht.
 *
 * Es trifft auch einen Chunk, der an einer abgerissenen Verbindung scheitert
 * (Webpack meldet dann ebenfalls „Loading chunk N failed"); die beiden Ursachen
 * sind an der Meldung nicht zu trennen.
 *
 * 🔴 Dieselbe Zeichenkette steht als KOPIE in
 * `frontend/tests/shell/route-splitting.spec.ts` und
 * `frontend/tests/design/catalog.spec.ts` — die Specs koennen `frontend/src`
 * nicht importieren. Bei einer Aenderung hier mitziehen.
 */
const CHUNK_MUSTER = /Loading (CSS )?chunk [\w.-]+ failed|ChunkLoadError|dynamically imported module|Importing a module script failed/i;

/**
 * Erkennt einen Chunk-Fehler — am NAMEN zuerst, dann an der Meldung.
 *
 * 🔴 webpack setzt an sein Fehlerobjekt `error.name = 'ChunkLoadError'`; die
 * Meldung traegt dann „Loading chunk N failed". Der Name wird ZUERST geprueft,
 * damit auch ein weitergereichtes Objekt ohne die Chunk-Zeichenkette erkannt
 * wird. Eine blosse Zeichenkette hat keinen Namen und geht ueber das Muster.
 *
 * @param {unknown} fehler — ein `Error`, eine Zeichenkette oder `null`
 * @returns {boolean} true, wenn der Fehler auf einen nicht geladenen Chunk deutet
 */
export function istChunkFehler(fehler) {
  if (fehler && fehler.name === 'ChunkLoadError') return true;
  const text = String((fehler && fehler.message) || fehler || '');
  return CHUNK_MUSTER.test(text);
}

/**
 * Umhuellt eine Importfunktion fuer `React.lazy`: ein Chunk-Fehler loest genau
 * ein Neuladen der Seite aus (Schleifenschutz ueber `sessionStorage`).
 *
 * @param {() => Promise<{default: React.ComponentType}>} importFn — das Argument
 *   von `React.lazy`, also `() => import('./Seite')`
 * @returns {() => Promise<{default: React.ComponentType}>} die umhuellte Importfunktion
 */
export function lazyMitNeuladen(importFn) {
  return () =>
    importFn().catch((fehler) => {
      // Nicht-Chunk-Fehler gehen unveraendert weiter — die `ErrorBoundary`
      // entscheidet wie bisher ueber die Karte.
      if (!istChunkFehler(fehler)) throw fehler;

      const jetzt = Date.now();
      let letzter;
      try {
        letzter = Number(window.sessionStorage.getItem(CHUNK_NEULADEN_SCHLUESSEL));
      } catch {
        // Ohne lesbaren Speicher gibt es keinen Schleifenschutz — also nicht laden.
        throw fehler;
      }
      if (Number.isFinite(letzter) && jetzt - letzter < FENSTER_MS) {
        // Schon einmal geladen, ohne Erfolg: jetzt ist es eine echte Stoerung.
        throw fehler;
      }
      try {
        // Erst unmittelbar vor dem Neuladen schreiben: nur so ist der Zeitstempel
        // eine Aussage ueber das, was wirklich passiert ist.
        window.sessionStorage.setItem(CHUNK_NEULADEN_SCHLUESSEL, String(jetzt));
      } catch {
        throw fehler;
      }
      // W5: VOR dem Neuladen einreihen (kein Timer, kein Warten, die
      // Warteschlange in `sessionStorage` ueberlebt das Neuladen, gesendet wird
      // danach). Ohne diese Zeile ist ein Chunk-Fehler fuer uns unsichtbar.
      meldeFehler({ art: 'chunk', fehler, nurEinreihen: true });
      window.location.reload();
      return new Promise((_, ablehnen) => {
        setTimeout(() => ablehnen(fehler), HAENGEN_MS);
      });
    });
}
